<script setup>
import {usePortalContentAndDesignStore} from "../../../../../../stores/partners/portalContentAndDesignStore";
import EditableResourceImageField
    from "../../../../portal-editable-fields/resources/EditableResourceImageField.vue";
import {ref, computed} from "vue";
import EditableTextField from "../../../../portal-editable-fields/EditableTextField.vue";
import EditableResourceTextEditorField
    from "../../../../portal-editable-fields/resources/EditableResourceTextEditorField.vue";

const props = defineProps({
    resource: {
        type: Object,
        required: true,
    },
});

const portalContentAndDesignStore = usePortalContentAndDesignStore();

const hoverMainContentFaqContainer = ref(false);

// Track which FAQ is currently open (null = none open)
const openFaqId = ref(null);

// Auto-open first valid item if setting is enabled
if (props.resource.first_item_open && props.resource.contents?.length) {
    const firstValid = props.resource.contents.find(item => item.question && item.answer);
    if (firstValid) {
        openFaqId.value = firstValid.unique_id ?? 0;
    }
}

const toggleFaq = (uniqueId) => {
    if (openFaqId.value === uniqueId) {
        openFaqId.value = null; // Close if already open
    } else {
        openFaqId.value = uniqueId; // Open this one
    }
};

const isFaqOpen = (uniqueId) => {
    return openFaqId.value === uniqueId;
};

const alignmentClass = computed(() => {
    switch (props.resource.alignment) {
        case 'center': return 'justify-center text-center';
        case 'right': return 'justify-end text-right';
        default: return 'justify-start text-left';
    }
});

const headerLayoutClass = computed(() => {
    const position = props.resource.main_image_position || 'left';
    const alignment = props.resource.alignment || 'left';

    if (position === 'top') {
        if (alignment === 'center') return 'flex flex-col items-center gap-4';
        if (alignment === 'right') return 'flex flex-col items-end gap-4';
        return 'flex flex-col items-start gap-4';
    }

    if (alignment === 'center') return 'flex flex-col items-center gap-4';
    return 'flex items-center gap-6';
});

const imageSizeClass = computed(() => {
    // Backwards compatibility: check for old retina resize setting
    if (props.resource.main_image_size === undefined && props.resource.main_image_retina_resize !== undefined) {
        return props.resource.main_image_retina_resize ? 'max-w-8' : 'max-w-16';
    }
    // New size setting
    const size = props.resource.main_image_size || 'medium';
    switch (size) {
        case 'small':
        case '@2x': return 'max-w-8';
        case 'large': return 'max-w-24';
        default: return 'max-w-16'; // medium
    }
});

// Get border color class based on theme
const borderColorClass = computed(() => {
    return portalContentAndDesignStore.getPageContentTextColorClass.replace('text-', 'border-').replace('custom-content-text', 'gray-200') + ' border-opacity-20';
});
</script>

<template>
    <div>
        <div :class="[headerLayoutClass, alignmentClass]"
             @mouseenter="hoverMainContentFaqContainer = true"
             @mouseleave="hoverMainContentFaqContainer = false"
             :id="resource.unique_id"
        >
            <editable-resource-image-field
                resource-image-path="main_image"
                :resource-content="resource"
                :img-class="imageSizeClass"
                img-name="main_image"
                :show-add-image-button="hoverMainContentFaqContainer"
            />

            <div class="space-y-1.5">
                <editable-text-field
                    v-if="resource.title"
                    text-type="h3"
                    :class="['text-xl md:text-2xl font-semibold', portalContentAndDesignStore.getPageContentTextColorClass]"
                    v-model="resource.title"
                />
                <editable-resource-text-editor-field
                    v-if="resource.description"
                    :class="[
                        'partnero-page-content-section text-base md:text-lg text-opacity-80 space-y-1',
                        portalContentAndDesignStore.getPageContentTextColorClass
                    ]"
                    :resource="resource"
                    resource-field="description"
                />
            </div>
        </div>

        <template v-if="resource.contents?.length">
            <div :class="[(resource.title || resource.description) ? 'mt-6' : '', 'divide-y', borderColorClass]">
                <div
                    v-for="content in resource.contents"
                    :key="content.unique_id"
                    class="py-4"
                >
                    <!-- Question (clickable header) -->
                    <button
                        type="button"
                        class="w-full flex items-center justify-between text-left gap-4 cursor-pointer"
                        @click="toggleFaq(content.unique_id)"
                    >
                        <span :class="['text-base md:text-lg font-semibold flex-1', portalContentAndDesignStore.getPageContentTextColorClass]">
                            {{ content.question }}
                        </span>
                        <span :class="['flex-shrink-0 text-xl font-light transition-transform duration-200', portalContentAndDesignStore.getPageContentTextColorClass]">
                            <svg v-if="isFaqOpen(content.unique_id)" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                            <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 4v16m8-8H4"/>
                            </svg>
                        </span>
                    </button>

                    <!-- Answer (collapsible content) -->
                    <div
                        v-show="isFaqOpen(content.unique_id)"
                        class="mt-3 pr-9"
                    >
                        <div
                            :class="['text-base opacity-80 partnero-page-content-section space-y-2', portalContentAndDesignStore.getPageContentTextColorClass]"
                            v-html="content.answer"
                        />
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>
